import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import TransferDetailModal from '../components/transfers/TransferDetailModal';
import transferService, {
  TransferListItem,
} from '../api/services/transfer.service';

const statusBadge = (status: string) => {
  if (status === 'completed') return 'bg-green-50 text-green-700';
  if (status === 'failed') return 'bg-red-50 text-red-700';
  return 'bg-gray-50 text-gray-700';
};

const Transfers: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'completed' | 'failed'
  >('all');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [detailId, setDetailId] = useState<string | null>(null);

  /* LIST */
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'transfers',
      page,
      limit,
      searchTerm,
      statusFilter,
      fromDate,
      toDate,
    ],
    queryFn: () =>
      transferService.getAll({
        page,
        limit,
        search: searchTerm || undefined,
        status: statusFilter,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const transfers = data?.transfers || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  };

  const handleSearchSubmit = () => {
    setSearchTerm(localSearch);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const handleClearFilters = () => {
    setLocalSearch('');
    setSearchTerm('');
    setStatusFilter('all');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const hasFilters =
    searchTerm ||
    statusFilter !== 'all' ||
    fromDate ||
    toDate;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transfers</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            View all balance transfers between users.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm" className="mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by name, phone, telegram id..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div className="relative">
            <CalendarDaysIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
              title="From date"
            />
          </div>

          <div className="relative">
            <CalendarDaysIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
              title="To date"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as any);
              setPage(1);
            }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>

          <button
            onClick={handleSearchSubmit}
            className="text-sm px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Search
          </button>

          {hasFilters && (
            <button
              onClick={handleClearFilters}
              className="text-sm px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card padding="sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-sm text-red-500">
              {error instanceof Error
                ? error.message
                : 'Failed to load transfers'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >
              Retry
            </button>
          </div>
        ) : transfers.length === 0 ? (
          <div className="text-center py-10">
            <ArrowsRightLeftIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No transfers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-3 py-2 font-medium">From</th>
                  <th className="px-3 py-2 font-medium">To</th>
                  <th className="px-3 py-2 font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((t: TransferListItem) => (
                  <tr
                    key={t.id}
                    className="border-b border-gray-50 hover:bg-gray-50/60"
                  >
                    <td className="px-3 py-3">
                      <p className="text-sm font-medium text-gray-900">
                        {t.senderName}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        {t.senderPhone || t.senderTelegramId}
                      </p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-sm font-medium text-gray-900">
                        {t.receiverName}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        {t.receiverPhone || t.receiverTelegramId}
                      </p>
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-900">
                      {t.currency} {t.amount.toLocaleString()}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${statusBadge(
                          t.status
                        )}`}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(t.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => setDetailId(t.id)}
                        className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors"
                        title="View"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-3 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.pages} ·{' '}
              {pagination.total} total
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(pagination.pages, p + 1))
                }
                disabled={page >= pagination.pages}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      <TransferDetailModal
        isOpen={!!detailId}
        transferId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
};

export default Transfers;